export type NotNull<T> = T extends null ? never : T;
export type NotUndefined<T> = T extends undefined ? never : T;
export type NotNullNorUndefined<T> = T extends null | undefined ? never : T;

export type EnumLikeType<T> = {
  [s: string]: T;
} | ArrayLike<T> | NonNullable<unknown>;

export module Utils {

  /**
   * Check whether a variable is undefined.
   */
  export function isUndefined<T>(obj: T | undefined): obj is undefined {
    return typeof obj === 'undefined';
  }

  /**
   * Check whether a variable is defined (ie is not undefined).
   * @see Utils.isUndefined
   */
  export function isDefined<T>(obj: T | undefined): obj is NotUndefined<T> {
    return !Utils.isUndefined(obj);
  }

  /**
   * Check whether a variable is null.
   */
  export function isNull<T>(obj: T | null): obj is null {
    return obj === null;
  }

  /**
   * Check whether a variable is not null.
   * @see Utils.isNull
   */
  export function isNotNull<T>(obj: T | null): obj is NotNull<T> {
    return !Utils.isNull(obj);
  }

  /**
   * Check that a variable is either null or undefined.
   */
  export function isNullOrUndefined<T>(obj: T | null | undefined): obj is (undefined | null) {
    return Utils.isUndefined(obj) || Utils.isNull(obj);
  }

  /**
   * Check that a variable is neither null nor undefined.
   * @see Utils.isNullOrUndefined
   */
  export function isNotNullOrUndefined<T>(obj: T | null | undefined): obj is NotNullNorUndefined<T> {
    return !Utils.isNullOrUndefined(obj);
  }

  /**
   * Check whether a variable is a number.
   */
  export function isNumber<T>(check: T | number): check is number {
    return typeof check === 'number';
  }

  /**
   * Check whether a variable is a string.
   */
  export function isString<T>(check: T | string): check is string {
    return typeof check === 'string' || check instanceof String;
  }

  /**
   * Check whether a string is blank (empty or undefined/null).
   */
  export function isBlank(obj: string | null | undefined): obj is (undefined | null | '') {
    return Utils.isUndefined(obj) || Utils.isNull(obj) || obj === '';
  }

  /**
   * Check whether a string is not blank.
   * @see Utils.isBlank
   */
  export function isNotBlank(obj: string | null | undefined): obj is string {
    return !Utils.isBlank(obj);
  }

  /**
   * Check whether a variable is an object.
   */
  export function isObject<T>(check: T | object): check is object {
    // (typeof null) returns 'object', so we have to add this additional check
    return typeof check === 'object' && Utils.isNotNull(check);
  }

  /**
   * Check whether a variable is an Error (ie an exception).
   */
  export function isError<T>(check: T | Error): check is Error {
    return check instanceof Error;
  }

  /**
   * Delete a property from an object, if it is not undefined.
   */
  export function deleteIfDefined<T>(object: T, property: keyof T): void {
    if (Utils.isDefined(object[property])) {
      delete object[property];
    }
  }

  /**
   * Set values into an object if these values are not defined in the object.
   * @param values Values to set (if undefined).
   * @param object Object to add values to.
   * @return Copy of the object, with the values assigned if they were not defined.
   */
  export function setValuesIfUndefined<T>(values: T, object: T | null | undefined): T {
    // Set values into a new object first
    const newObject = Object.assign({}, values);
    // Override non-undefined fields of object to keep specified values
    if (Utils.isNotNullOrUndefined(object) && Utils.isObject(object)) {
      Object.keys(object)
        .filter((key: string) => Object.hasOwn(object, key))
        .forEach((key: string) => {
          // eslint-disable-next-line max-len
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
          (newObject as any)[key] = (object as any)[key];
        });
    }
    return newObject;
  }

  /**
   * Perform a copy of an object.
   */
  export function copy<T>(object: T): T {
    return Object.assign({}, object);
  }

  /**
   * Perform a deep copy of an object.
   */
  export function deepCopy<T = object>(object: T): T {
    if (Utils.isNotNullOrUndefined(window.structuredClone)) {
      // As of March 2022
      return structuredClone(object);
    }
    return JSON.parse(JSON.stringify(object)) as T;
  }

  /**
   * Perform a shallow copy of an object.
   */
  export function shallowCopy<T = object>(object: T): T {
    return {...object};
  }

  /**
   * Returns the provided value, or a default one if the provided value is either null or undefined.
   */
  export function valueOr<T>(object: T | undefined | null, defaultValue: T): T {
    return Utils.isNullOrUndefined(object) ? defaultValue : object;
  }

  /**
   * Returns the provided value if the condition is true, or undefined if the condition is false.
   */
  export function valueIf<T>(object: T, condition: boolean): T | undefined {
    return condition ? object : undefined;
  }

  /**
   * Converts a boolean to a number.
   */
  export function booleanToNumber(b: boolean): number {
    return b ? 1 : 0;
  }

  /**
   * Converts an enum value to the name of this value in the enum.
   * @warning: stringValueToEnumValue is not the mirror of this method since this method handle enum key
   */
  export function enumName<T>(
    enumValue: T | undefined | null,
    enumObject: EnumLikeType<T>,
  ): string | undefined {
    if (Utils.isNullOrUndefined(enumValue)) {
      return undefined;
    }
    const enumEntries = Object.entries(enumObject);
    for (const entry of enumEntries) {
      if (entry[1] === enumValue) {
        return entry[0];
      }
    }
    return undefined;
  }

  /**
   * Converts a string value of an enum to the corresponding value in the Enum type
   * @warning: enumName is not the mirror of this method since this method handle enum value
   */
  export function stringValueToEnumValue<T>(value: string, enumObject: EnumLikeType<T>): T | undefined {
    const enumValue: [string, T] | undefined = Object.entries(enumObject)
      .find((entry: [string, T | string]) => entry[1] === value);

    if (Utils.isNullOrUndefined(enumValue)) {
      return undefined;
    }

    return enumValue[1] as unknown as T;
  }

  /**
   * Checks if a string value exist in corresponding Enum type
   */
  export function enumValueExists<T>( value: string, enumObject: EnumLikeType<T>): boolean {
    return Utils.isNotNullOrUndefined(Utils.stringValueToEnumValue(value, enumObject));
  }

  /**
   * Get all the values of an enum.
   */
  export function enumValues<T>(enumObject: { [s: string]: string | T; }): Array<T> {
    const enumValues: Array<T> = [];
    for (const key of Object.keys(enumObject)) {
      if (!isNaN(Number(key))) {
        // Ignore numbers for numeric enums
        continue;
      }
      enumValues.push(enumObject[key] as T);
    }
    return enumValues;
  }

  /**
   * Copy a string and makes its first character upper case.
   */
  export function upperFirstCharacter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  /**
   * Copy a string and makes its first character lower case.
   */
  export function lowerFirstCharacter(string: string): string {
    return string.charAt(0).toLowerCase() + string.slice(1);
  }

  /**
   * Check that a value is in the range [first,last].
   */
  export function isBetween(first: number, last: number, value: number): boolean {
    return value >= first && value <= last;
  }

  /**
   * Asynchronous version of setTimeout.
   * @param ms Requested time, in milliseconds.
   * @return Promise that resolve after the requested time.
   */
  export function timeout(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Converts a blob to a base64 string.
   */
  export function blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((
      resolve: (value: string) => void,
      reject: (reason?: any) => void,
    ): void => {
      const reader = new FileReader();
      reader.onloadend = (): void => {
        resolve(reader.result as string);
      };
      reader.onerror = (): void => {
        reject(reader.error);
      };
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Converts an array buffer to a base64 string.
   */
  export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary: string = '';
    const bytes = new Uint8Array(buffer);
    const len: number = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Converts an array buffer to a string containing its binary representation in hexadecimal.
   */
  export function arrayBufferToString(buffer: ArrayBuffer): string {
    const hashArray = Array.from(new Uint8Array(buffer));
    return hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Generates a random string.
   * @param length Length of the string to generate
   * @param characters Characters that the string can contain
   */
  export function randomString(
    length: number,
    characters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
  ): string {
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  /**
   * Get a number from a string.
   */
  export function retrieveNumberInString(text: string): number | null {
    const matches = text.match(/(\d+)/);
    return Utils.isNotNullOrUndefined(matches) ? parseInt(matches[0]) : null;
  }

  /**
   * Removes the duplicate values in an array.
   * @warning The elements order is not necessarily conserved
   */
  export function removeDuplicatedValues<T>(duplicatedValuesArray: Array<T>): Array<T> {
    return [...new Set(duplicatedValuesArray)];
  }

  /**
   * Get a file name from a file path.
   */
  export function fileNameFromPath(filePath: string): string {
    let splits = filePath.split('/');
    splits = splits[splits.length - 1].split('\\');
    return splits[splits.length - 1];
  }

}
